import type { PropsWithChildren, ReactElement } from 'react';
import React, { useMemo } from 'react';
import { authorisableUserWithSiteAdminRemoved } from 'policies/authorisable-user.helpers';
import { useAuth } from 'services/providers/auth.provider';
import useCurrentAreaAccess from 'services/providers/hooks/use-current-area-access';
import useCurrentOrganisationAccess from 'services/providers/hooks/use-current-organisation-access';
import { accessContext } from './access.helpers';
import type { AccessContext } from './access.types';

const AreaAccessContext = React.createContext(accessContext);

/**
 * A similar provider to PageAccessProvider that can be used to authorise a specified area.
 */
export default function AreaAccessProvider({
  areaId,
  children
}: PropsWithChildren<{ areaId: number }>): ReactElement {
  const { me, isLoading: isAuthLoading } = useAuth();

  const { currentOrganisationRoles } = useCurrentOrganisationAccess();

  // Use the specified areaID prop to load the area-level roles for the specified area:
  const { currentArea, currentAreaRoles, belongsToCurrentArea, isCurrentAreaAccessLoading } =
    useCurrentAreaAccess(areaId);

  const isLoading = isAuthLoading || isCurrentAreaAccessLoading;

  // Authorisation focused user that can be passed to authorisation policies constructed in the
  // context of the current area:
  const authorisableUser = useMemo(
    () => ({
      id: me?.id,
      roles: currentOrganisationRoles,
      areaLevelRoles: currentAreaRoles,
      belongsToCurrentArea
    }),
    [me?.id, currentOrganisationRoles, currentAreaRoles, belongsToCurrentArea]
  );

  // A copy of the authorisableUser, but with the site admin role removed from the user's roles.
  // This is useful for comparative purposes (e.g. when showing the blue site admin only buttons):
  const userMinusSiteAdmin = useMemo(
    () => authorisableUserWithSiteAdminRemoved(authorisableUser),
    [authorisableUser]
  );

  // Authorisation focused area that can be passed to authorisation policies:
  const authorisableArea = useMemo(
    () => ({
      id: currentArea?.id,
      identifier: currentArea?.identifier,
      private: currentArea?.private
    }),
    [currentArea]
  );

  const value = useMemo(
    () => ({ authorisableUser, userMinusSiteAdmin, authorisableArea, isLoading }),
    [authorisableUser, userMinusSiteAdmin, authorisableArea, isLoading]
  );

  return <AreaAccessContext.Provider value={value}>{children}</AreaAccessContext.Provider>;
}

export const useAreaAccess = (): AccessContext => React.useContext(AreaAccessContext);
